import { useEffect, useRef, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Alert from "@mui/material/Alert";
import { Html5QrcodeScanner } from "html5-qrcode";

import PageHeader from "components/PageHeader";
import { API_BASE, API_HOST } from "config/api";

const SCANNER_ELEMENT_ID = "qr-checkin-scanner";

function QrCheckIn() {
  const [lastResult, setLastResult] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const isProcessingRef = useRef(false);
  const scannerRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const handleDecoded = useCallback(async (decodedText) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/attendance/qr-checkin`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ qr_token: decodedText }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setStatusMessage({ type: "error", text: body.message || "Invalid QR code" });
        setLastResult(null);
      } else {
        setLastResult(body.member);
        setStatusMessage({ type: "success", text: "Checked in successfully" });
      }
    } catch (e) {
      setStatusMessage({ type: "error", text: e.message });
    } finally {
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 2000);
    }
  }, []);

  useEffect(() => {
    // html5-qrcode starts the camera's <video> element asynchronously (it
    // awaits getUserMedia + play() internally). If this component unmounts
    // before that resolves, the library's own unhandled play() promise
    // rejects with a benign "play() request was interrupted because the
    // media was removed from the document" — harmless, but logs as an
    // uncaught error. Swallow only that exact rejection here.
    const swallowInterruptedPlay = (event) => {
      const message = String(event.reason?.message || "");
      if (
        event.reason?.name === "AbortError" &&
        message.includes("play() request was interrupted")
      ) {
        event.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", swallowInterruptedPlay);

    const scanner = new Html5QrcodeScanner(SCANNER_ELEMENT_ID, { fps: 10, qrbox: 250 }, false);
    scanner.render(
      (decodedText) => handleDecoded(decodedText),
      () => {}
    );
    scannerRef.current = scanner;

    return () => {
      window.removeEventListener("unhandledrejection", swallowInterruptedPlay);
      scannerRef.current?.clear().catch(() => {});
    };
  }, [handleDecoded]);

  return (
    <Box>
      <PageHeader
        title="QR Check-In"
        subtitle="Point the camera at a member's personal QR badge to check them in."
      />

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Card sx={{ p: 2, flex: "1 1 360px", maxWidth: 420 }}>
          <div id={SCANNER_ELEMENT_ID} />
        </Card>

        <Card sx={{ p: 3, flex: "1 1 300px", textAlign: "center" }}>
          {statusMessage && (
            <Alert severity={statusMessage.type} sx={{ mb: 2 }}>
              {statusMessage.text}
            </Alert>
          )}
          {lastResult ? (
            <Box>
              <Avatar
                src={lastResult.photo ? `${API_HOST}/storage/${lastResult.photo}` : undefined}
                sx={{ width: 80, height: 80, mx: "auto", mb: 1.5, fontSize: 28 }}
              >
                {lastResult.first_name?.[0]}
              </Avatar>
              <Typography variant="h6" fontWeight={700}>
                {lastResult.first_name} {lastResult.surname}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lastResult.memberID}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Scan results will appear here.
            </Typography>
          )}
        </Card>
      </Box>
    </Box>
  );
}

export default QrCheckIn;
